import { useState } from 'react';
import '../../styles/pages/Admin/Dashboard.scss';
import SERVER from '../../hooks/useServer';
import axios from 'axios';
import TranslationButton from './TranslationButton';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AdminCategories = ({ activeTab, categories, setCategories }) => {
    const [loading, setLoading] = useState(null)
    const [subCategories, setSubCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    // Filter and search states
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPageCommandes, setCurrentPageCommandes] = useState(1);
    // Calculate paginated commandes
    const indexOfLastCommande = currentPageCommandes * itemsPerPage;
    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    // Filter and search functions
    const filteredCategories = categories.filter(cat => 
        cat.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Paginated data
    const currentCategories = filteredCategories.slice(indexOfFirstItem, indexOfLastItem);

    // Change page
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Image modal handlers
    const openImageModal = (imageUrl) => {
        setSelectedImage(imageUrl);
        setIsModalOpen(true);
    };
    // Form states
    const [newCategory, setNewCategory] = useState({title: '', image: ''});

    const handleAddCategory = async () => {
        // Validation
        if (!newCategory.title.trim()) {
            toast.error('Le nom de la catégorie est requis');
            return;
        }

        // Check for duplicate category name
        const duplicateCategory = categories.find(
            cat => cat.title.toLowerCase().trim() === newCategory.title.toLowerCase().trim()
        );
        if (duplicateCategory) {
            toast.error('Une catégorie avec ce nom existe déjà');
            return;
        }

        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('title', newCategory.title);
            if (newCategory.imageFile) {
                formData.append('image', newCategory.imageFile);
            }

            const response = await axios.post(`${SERVER}/categories`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            setCategories([...categories, response.data]);
            setNewCategory({title: '', image: ''});
            toast.success('Catégorie ajoutée avec succès');
            setError(null);
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.message;
            setError(errorMsg);
            toast.error(`Erreur: ${errorMsg}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (type, id) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ? Toutes les sous-catégories et produits associés seront également supprimés.')) {
            return;
        }

        try {
            setLoading(true);
            let endpoint = '';
            switch(type) {
                case 'category':
                    endpoint = `${SERVER}/categories/${id}`;
                    break;
                default:
                    return;
            }

            await axios.delete(endpoint);
            
            // Update local state
            switch(type) {
                case 'category':
                    setCategories(categories.filter(cat => cat.id !== id));
                    setSubCategories(subCategories.filter(sub => sub.category_id !== id));
                    setProducts(products.filter(product => product.category_id !== id));
                    toast.success('Catégorie supprimée avec succès');
                    break;
                default:
                    break;
            }
            setError(null);
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.message;
            setError(errorMsg);
            toast.error(`Erreur lors de la suppression: ${errorMsg}`);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (type) => {
        // Validation
        if (!newCategory.title.trim()) {
            toast.error('Le nom de la catégorie est requis');
            return;
        }

        // Check for duplicate category name (excluding current category)
        const duplicateCategory = categories.find(
            cat => cat.id !== selectedCategory.id && 
            cat.title.toLowerCase().trim() === newCategory.title.toLowerCase().trim()
        );
        if (duplicateCategory) {
            toast.error('Une catégorie avec ce nom existe déjà');
            return;
        }

        try {
            setLoading(true);
            const formData = new FormData();
            let response;

            switch(type) {
                case 'category':
                    formData.append('title', newCategory.title);
                    if (newCategory.imageFile) {
                        formData.append('image', newCategory.imageFile);
                    } else if (newCategory.existing_image) {
                        formData.append('existing_image', newCategory.existing_image);
                    }

                    response = await axios.put(`${SERVER}/categories/${selectedCategory.id}`, formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                    
                    setCategories(categories.map(cat => 
                        cat.id === response.data.id ? response.data : cat
                    ));
                    toast.success('Catégorie mise à jour avec succès');
                    break;
                default:
                    return;
            }
            // Reset form states
            setNewCategory({title: '', image: ''});
            setIsEditing(false);
            setSelectedCategory(null);
            setError(null);
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.message;
            setError(errorMsg);
            toast.error(`Erreur: ${errorMsg}`);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (type, item) => {
        setIsEditing(true);
        switch(type) {
            case 'category':
                setSelectedCategory(item);
                setNewCategory({
                    title: item.title,
                    image: item.banner_image,
                    existing_image: item.banner_image
                });
                toast.info('Mode édition activé');
                break;
            default:
                break;
        }
    };

    const closeImageModal = () => {
        setIsModalOpen(false);
        setSelectedImage(null);
    };

    if (loading) return <div className="loading">Chargement...</div>;
    if (error) return <div className="error">Erreur: {error}</div>;
    return (
        <div className='admin'>
            {isModalOpen && (
                <div className="image-modal" onClick={closeImageModal}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <span className="close-button" onClick={closeImageModal}>&times;</span>
                        <img src={`${SERVER}/${selectedImage}`} alt="Full size" />
                    </div>
                </div>
            )}
            {/* Search Bar */}
            <div className="search-filter-container">
                <input
                    type="text"
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    className="search-input"
                />
            </div>

            {/* Categories Tab */}
            {activeTab === 'categories' && (
                <div className="admin-section">
                    <h2>Catégories</h2>
                    
                    {/* Category Form */}
                    <div className="admin-form">
                        <input
                            type="text"
                            placeholder="Nom de la catégorie"
                            value={newCategory.title}
                            onChange={(e) => setNewCategory({...newCategory, title: e.target.value})}
                        />
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    setNewCategory({
                                        ...newCategory,
                                        image: URL.createObjectURL(file),
                                        imageFile: file
                                    });
                                }
                            }}
                        />
                        {isEditing ? (
                            <>
                                <button onClick={() => handleUpdate('category')}>Mettre à jour</button>
                                <button onClick={() => { 
                                    setIsEditing(false); 
                                    setNewCategory({title: '', image: ''}); 
                                }}>Annuler</button>
                            </>
                        ) : (
                            <button onClick={handleAddCategory}>Ajouter une catégorie</button>
                        )}
                    </div>
                    
                    {/* Categories Table */}
                    <div className="admin-table-container">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '100px' }}>Image</th>
                                    <th>Nom</th>
                                    <th style={{ width: '200px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentCategories.map((category) => (
                                    <tr key={category.id}>
                                        <td>
                                            {category.banner_image && (
                                                <img 
                                                    src={`${SERVER}/${category.banner_image}`} 
                                                    alt={category.title} 
                                                    className="table-image"
                                                    onClick={() => openImageModal(category.banner_image)}
                                                />
                                            )}
                                        </td>
                                        <td>{category.title}</td>
                                        <td>
                                            <div className="actions-container">
                                                <div className="admin-actions">
                                                    <button onClick={() => handleEdit('category', category)}>Modifier</button>
                                                    <button onClick={() => handleDelete('category', category.id)}>Supprimer</button>
                                                </div>
                                                <TranslationButton
                                                    data={category}
                                                    type="category"
                                                    onTranslationSaved={() => {
                                                        window.location.reload();
                                                    }}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        
                        {/* Pagination */}
                        <div className="pagination">
                            {Array.from({ length: Math.ceil(filteredCategories.length / itemsPerPage) }).map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => paginate(index + 1)}
                                    className={currentPage === index + 1 ? 'active' : ''}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

};

export default AdminCategories;